import {Action, Selector, State, StateContext, Store} from '@ngxs/store';
import {HttpClient} from '@angular/common/http';
import {catchError, tap} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import * as Act from './orders.actions';
import {IEdiOrder} from '../../interfaces/EDI-order.interface';
import {IOrdersHistory} from '../../interfaces/orders-history.inteface';
import {endpoints} from '../../constants/endpoints';
import {AuthState} from '../auth/auth.state';
import {IFileUploadObject} from '../../components/file-uploader/file-upload-object.interface';
import { getUTC } from '../../helpers/helpers';
import { IProvider } from '../../interfaces/provider.interface';
import { IOrderSaleReturn } from 'src/app/inventory/interfaces/order-sale-return.interface';
import { IOrderSaleReturnDetailsData } from 'src/app/inventory/interfaces/order-sale-return-details-data.interface';
import { IReverseDistributor } from '../../interfaces/reverse-distributor.interface';
import { IPlaceOfDestruction } from '../../interfaces/destruction-place.interface';
import { IOrderInventoryDestructionListModel } from 'src/app/inventory/interfaces/inventory-destructions-list-model.interface';
import { IOrderDestructionDetails as IOrderForInventoryDestructionDetails } from 'src/app/inventory/interfaces/order-destruction-details.interface';
import { IResponse } from '../../interfaces/IResponse';
import { IGetFileMetadataResponse } from '../../interfaces/get-file-metadata-response.interface';
import { IOrderParty, OrderPartyDetail, OrderPartyTypeEnum } from 'src/app/inventory/interfaces/order-party-details.interface';
import { ISelectedOrderParty } from 'src/app/inventory/interfaces/selected-order-party.interface';

export interface OrdersStateModel {
  loading: boolean;
  userOrdersCount: number;
  ordersHistoryList: IOrdersHistory[];
  receiveEDI: IEdiOrder;
  orderFiles: IFileUploadObject[];
  orderDetails: IEdiOrder;
  success: boolean;
  recentRequestedAttachmentMetadata: IGetFileMetadataResponse;
  providers: IProvider[];
  reverseDistributors: IReverseDistributor[];
  placesOfDestruction: IPlaceOfDestruction[];
  createdOrderID: string;
  salesReturns: IOrderSaleReturn[];
  orderSaleOrReturnDetailsData: IOrderSaleReturnDetailsData;
  inventoryDestructions: IOrderInventoryDestructionListModel[];
  inventoryDestructionDetailsData: IOrderForInventoryDestructionDetails;
  buyers: IOrderParty[];
  ediSuppliers: IOrderParty[];
  addedModifiedOrderParty: ISelectedOrderParty;
  orderPartyDetails: OrderPartyDetail;
  latestSupplierOrEdiBuyerDetails?: OrderPartyDetail;
}

@State<OrdersStateModel>({
  name: 'orders',
})

@Injectable()
export class OrdersState {

  constructor(private http: HttpClient, private store: Store) {
  }

  @Selector()
  static userOrders(state: OrdersStateModel): number {
    return state.userOrdersCount;
  }

  @Selector()
  static ordersHistoryList(state: OrdersStateModel): IOrdersHistory[] {
    return state.ordersHistoryList;
  }

  @Selector()
  static receiveEDI(state: OrdersStateModel): IEdiOrder {
    return state.receiveEDI;
  }

  @Selector()
  static orderFiles(state: OrdersStateModel): IFileUploadObject[] {
    return state.orderFiles;
  }

  @Selector()
  static orderDetails(state: OrdersStateModel): IEdiOrder {
    return state.orderDetails;
  }

  @Selector()
  static loading(state: OrdersStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static success(state: OrdersStateModel): boolean {
    return state.success;
  }

  @Selector()
  static recentRequestedAttachmentMetadata(state: OrdersStateModel): IGetFileMetadataResponse {
    return state.recentRequestedAttachmentMetadata;
  }

  @Selector()
  static providers(state: OrdersStateModel): IProvider[] {
    return state.providers || [];
  }

  @Selector()
  static reverseDistributors(state: OrdersStateModel): IReverseDistributor[] {
    return state.reverseDistributors || [];
  }

  @Selector()
  static placesOfDestruction(state: OrdersStateModel): IPlaceOfDestruction[] {
    return state.placesOfDestruction || [];
  }

  @Selector()
  static createdOrderID(state: OrdersStateModel): string {
    return state.createdOrderID;
  }

  @Selector()
  static salesReturns(state: OrdersStateModel): IOrderSaleReturn[] {
    return state.salesReturns;
  }

  @Selector()
  static inventoryDestructions(state: OrdersStateModel): IOrderInventoryDestructionListModel[] {
    return state.inventoryDestructions;
  }

  @Selector()
  static orderSaleOrReturnDetailsData(state: OrdersStateModel): IOrderSaleReturnDetailsData {
    return state.orderSaleOrReturnDetailsData;
  }

  @Selector()
  static orderForInventoryDestructionDetailsData(state: OrdersStateModel): IOrderForInventoryDestructionDetails {
    return state.inventoryDestructionDetailsData;
  }

  @Selector()
  static buyers(state: OrdersStateModel): IOrderParty[] {
    return state.buyers.filter(x => x.type === OrderPartyTypeEnum.Buyer);
  }

  @Selector()
  static ediSuppliers(state: OrdersStateModel): IOrderParty[] {
    return state.ediSuppliers;
  }


  @Selector()
  static addedModifiedOrderParty(state: OrdersStateModel): ISelectedOrderParty {
    return state.addedModifiedOrderParty;
  }

  @Selector()
  static orderPartyDetails(state: OrdersStateModel): OrderPartyDetail {
    return state.orderPartyDetails;
  }

  @Selector()
  static latestSupplierOrEdiBuyerDetails(state: OrdersStateModel): OrderPartyDetail | null {
    return state.latestSupplierOrEdiBuyerDetails;
  }

  @Action(Act.GetUserOrders)
  getUserData(ctx: StateContext<OrdersStateModel>, {}: Act.GetUserOrders): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    if (pharmacyId === 'undefined' || !pharmacyId) {
      return;
    }

    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.totalProcessed + '?id=' + pharmacyId)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            userOrdersCount: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetOrderHistoryList)
  getOrderHistoryList(ctx: StateContext<OrdersStateModel>, {searchString}: Act.GetOrderHistoryList): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.orderList + '?id=' + pharmacyId)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            ordersHistoryList: resp?.value,
            loading: false,
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetEDIOrder)
  getReceiveEDI(ctx: StateContext<OrdersStateModel>, {id}: Act.GetEDIOrder): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);
    ctx.patchState({loading: true, receiveEDI: null});
    return this.http.post(endpoints.order.getOrder, {id, pharmacyId})
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            receiveEDI: {...resp?.value, shippedDate: getUTC(resp?.value?.shippedDate)},
            orderFiles: resp?.value?.orderAttachments ?? ctx.getState().orderFiles
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetEDIAttachment)
  getEDIAttachment(ctx: StateContext<OrdersStateModel>, {attachment}: Act.GetEDIAttachment): Observable<any> {
    ctx.patchState({ recentRequestedAttachmentMetadata: null, loading: true});
    return this.http.post(endpoints.order.getAttachment, {id: attachment.id, name: attachment.name})
      .pipe(
        tap((responseData: any) => {
          if (responseData?.value) {
            ctx.patchState({
              recentRequestedAttachmentMetadata: responseData.value
            });
          }
          ctx.patchState({loading: false});

        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveEDIAttachments)
  saveEDIAttachments(ctx: StateContext<OrdersStateModel>, {files, payload}: Act.SaveEDIAttachments): Observable<any> {
    const pharmacyId = this.store.selectSnapshot(AuthState.pharmacyId);

    ctx.patchState({orderFiles: files, loading: true});

    const request = {
      orderId: payload.orderId,
      pin: payload.pin,
      pharmacyId,
      csos: payload.csos,
      orderAttachments: files.map(x => ({
         id: x.id,
         name: x.name, 
         type: x.type, 
         otherType: x.otherType, 
         isReplaced: x.isReplaced, 
         isSigned: x.isSigned,
         createDate: x.createDate
        }))
    };

    return this.http.patch(endpoints.order.processAttachment, request)
      .pipe(
        tap((responseData: any) => {
          const responseCreatedAttachments = responseData?.value?.createdOrderAttachments;
          const modifiedAttachment = responseData?.value?.modifiedAttachment;
          if (!!responseCreatedAttachments.length) {
            ctx.patchState({
              orderFiles: ctx.getState().orderFiles?.map((x) => {
                const matchedAttachment = responseCreatedAttachments?.find((y) => x?.id === y?.id);
                if (matchedAttachment) {
                  return {
                    ...x,
                    downloadUrl: matchedAttachment?.url ?? x.downloadUrl,
                    name: matchedAttachment?.name ?? x.name,
                    user: responseData?.value?.user,
                    createDate: matchedAttachment?.createDate,
                    uploadCompleted: undefined,
                  };
                }
               return x;
              }),
            });
          } 
          if (modifiedAttachment) { //update the user
            ctx.patchState({
              orderFiles: ctx.getState().orderFiles?.map(x => (x.id === modifiedAttachment?.id ? {
                ...x,
                downloadUrl: modifiedAttachment?.url,
                name: modifiedAttachment.name,
                user: {firstName: modifiedAttachment?.modifierFirstName, lastName: modifiedAttachment?.modifierLastName},
                lastModificationDate: modifiedAttachment?.lastModificationDate,
              } : x)),
            });
          }
          ctx.patchState({loading: false});
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }


  @Action(Act.GetInventoryOrderDetails)
  getInventoryOrderDetails(ctx: StateContext<OrdersStateModel>, {id}: Act.GetInventoryOrderDetails): Observable<any> {
    ctx.patchState({loading: true, orderDetails: null});

    return this.http.post(endpoints.order.getOrder, {id})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            orderDetails: responseData?.value,
            loading: false,
            orderFiles: responseData?.value?.orderAttachments ?? ctx.getState().orderFiles
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SetInventoryOrderDetails)
  setInventoryOrderDetails(ctx: StateContext<OrdersStateModel>, {orderDetails}: Act.SetInventoryOrderDetails): void {
    ctx.patchState({loading: true, orderDetails});
  }

  @Action(Act.ReceiveEDIOrder)
  receiveEDIOrder(ctx: StateContext<OrdersStateModel>, {receiveEDI}: Act.ReceiveEDIOrder): Observable<any> {
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    ctx.patchState({loading: true, success: false});
    receiveEDI.pharmacyId = pharmacyID;

    return this.http.post(endpoints.order.receiveOrder, receiveEDI)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            success: resp?.success
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.AddEDIOrder)
  addEDIOrder(ctx: StateContext<OrdersStateModel>, {transactions}: Act.AddEDIOrder): Observable<any> {
    ctx.patchState({loading: true, success: false});
    const pharmacyID = this.store.selectSnapshot(AuthState.pharmacyId);
    transactions.forEach(el => {
      el.pharmacyId = pharmacyID;
      el.createByID = this.store.selectSnapshot(AuthState.activeUser).id;
    });

    return this.http.put(endpoints.transaction.createTransaction, transactions)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            success: resp?.success
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.DecrementProcessedOrdersCount)
  decrementProcessedOrdersCount(ctx: StateContext<OrdersStateModel>, {}: Act.DecrementProcessedOrdersCount): void {
    const userOrdersCount = ctx.getState().userOrdersCount - 1;
    ctx.patchState({userOrdersCount});
  }

  @Action(Act.SetFileLoadingState)
  setFileLoadingState(ctx: StateContext<OrdersStateModel>, {id}: Act.SetFileLoadingState): void {
    const files = [...ctx.getState().orderFiles];
    ctx.patchState({
      orderFiles: files.map(x => ( x.id === id ? {...x, isUploading: true, uploadCompleted: false }: x))
    });
  }

  @Action(Act.UpdateUploadedAttachment)
  updateUploadedAttachment(ctx: StateContext<OrdersStateModel>, {id}: Act.UpdateUploadedAttachment): Observable<any> {
    ctx.patchState({
      orderFiles: ctx.getState()?.orderFiles?.map(x => ( x.id === id ? {...x, isUploading: false }: x)),
      loading: true, 
      success: false
    });
    return this.http.patch<IResponse<boolean>>(endpoints.order.updateUploadedAttachment + '/' + id, {})
      .pipe(
        tap((resp) => {
          ctx.patchState({
            loading: false,
            success: resp?.success,
            orderFiles: ctx.getState()?.orderFiles?.map(x => ( x.id === id ? {...x, uploadCompleted: resp?.value, downloadUrl: null }: x)),
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.UpdateOrder)
  updateOrder(ctx: StateContext<OrdersStateModel>, {order}: Act.UpdateOrder): Observable<any> {
    ctx.patchState({loading: true, success: false});
    return this.http.patch(endpoints.order.updateOrder, order)
      .pipe(
        tap((resp: any) => {
          ctx.patchState({
            loading: false,
            success: resp?.success
          });
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreateProvider)
  createProvider(ctx: StateContext<OrdersStateModel>, {name}: Act.CreateProvider): Observable<IProvider> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.order.createProvider, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            const providers = [...ctx.getState().providers || []];
            providers.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              providers: providers
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreateReverseDistributor)
  createReverseDistributor(ctx: StateContext<OrdersStateModel>, {name}: Act.CreateReverseDistributor): Observable<IReverseDistributor> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.order.createReverseDistributor, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            const reverseDistributors = [...ctx.getState().reverseDistributors || []];
            reverseDistributors.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              reverseDistributors
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.CreatePlaceOfDestruction)
  createPlaceOfDestruction(ctx: StateContext<OrdersStateModel>, {name}: Act.CreatePlaceOfDestruction): Observable<IPlaceOfDestruction> {
    ctx.patchState({loading: true});
    return this.http.put(endpoints.order.createPlaceOfDestruction, {name})
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            const placesOfDestruction = [...ctx.getState().placesOfDestruction || []];
            placesOfDestruction.unshift({id: responseData?.value, name: name});
            ctx.patchState({
              placesOfDestruction
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }


  @Action(Act.GetProvidersList)
  getProvidersList(ctx: StateContext<OrdersStateModel>, {}: Act.GetProvidersList): Observable<IProvider[]> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.getProviders)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({
              providers: responseData?.value
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetReverseDistributorsList)
  getReverseDistributorsList(ctx: StateContext<OrdersStateModel>, {}: Act.GetReverseDistributorsList): Observable<IReverseDistributor[]> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.getReverseDistributors)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({
              reverseDistributors: responseData?.value
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetPlacesOfDestructionList)
  getPlacesOfDestructionList(ctx: StateContext<OrdersStateModel>, {}: Act.GetPlacesOfDestructionList): Observable<IPlaceOfDestruction[]> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.getPlacesOfDestruction)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({
              placesOfDestruction: responseData?.value
            });
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.SaveNewSaleOrReturn)
  saveNewSaleOrReturn(ctx: StateContext<OrdersStateModel>, {payload}: Act.SaveNewSaleOrReturn): Observable<any> {
    ctx.patchState({loading: true, createdOrderID: null});
    return this.http.put(endpoints.order.saveNewSaleOrReturn, payload)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({createdOrderID: responseData.value});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.DestroyInventory)
  destroyInventory(ctx: StateContext<OrdersStateModel>, {payload}: Act.DestroyInventory): Observable<any> {
    ctx.patchState({loading: true, createdOrderID: null});
    return this.http.put(endpoints.order.destroyInventory, payload)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({createdOrderID: responseData.value});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetSalesToProviders)
  getSalesToProviders(ctx: StateContext<OrdersStateModel>, {}: Act.GetSalesToProviders): Observable<IOrderSaleReturn> {
    ctx.patchState({loading: true, salesReturns: []});
    return this.http.get(endpoints.order.getSalesToProviders)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({salesReturns: responseData.value?.map(x => ({...x, soldReturnedTo: x?.providerName || x?.buyerName}))});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetSalesToReverseDistributors)
  getSalesToReverseDistributors(ctx: StateContext<OrdersStateModel>, {}: Act.GetSalesToReverseDistributors): Observable<IOrderSaleReturn> {
    ctx.patchState({loading: true, salesReturns: []});
    return this.http.get(endpoints.order.getSalesToReverseDistributors)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({salesReturns: responseData.value?.map(x => ({...x, soldReturnedTo: x?.reverseDistributorName || x?.buyerName}))});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetReturnsToWholesalers)
  getReturnsToWholesalers(ctx: StateContext<OrdersStateModel>, {}: Act.GetReturnsToWholesalers): Observable<IOrderSaleReturn> {
    ctx.patchState({loading: true, salesReturns: []});
    return this.http.get(endpoints.order.getReturnsToWholesalers)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({salesReturns: responseData.value?.map(x => ({...x, soldReturnedTo: x?.wholesalerName || x?.buyerName}))});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetInventoryDestructions)
  getInventoryDestructions(ctx: StateContext<OrdersStateModel>, {}: Act.GetInventoryDestructions): Observable<IOrderInventoryDestructionListModel[]> {
    ctx.patchState({loading: true});
    return this.http.get(endpoints.order.getInventoryDestructions)
      .pipe(
        tap((responseData: any) => {
          ctx.patchState({
            loading: false,
          });
          if(responseData?.success){
            ctx.patchState({inventoryDestructions: responseData.value});
          }
        }),
        catchError((err) => {
          ctx.patchState({loading: false});
          return of(null);
        }),
      );
  }

  @Action(Act.GetSaleToProviderDetails)
  getSaleToProviderDetails(ctx: StateContext<OrdersStateModel>, {id}: Act.GetSaleToProviderDetails): Observable<IOrderSaleReturnDetailsData> {
      ctx.patchState({loading: true, orderSaleOrReturnDetailsData: null});
      return this.http.get(endpoints.order.getSaleToProviderDetails + "?id=" + id)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({
                orderSaleOrReturnDetailsData: {...responseData?.value, soldReturnedTo: responseData?.value?.providerName},
                orderFiles: responseData?.value?.attachments
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetSaleToReverseDistributorDetails)
    getSaleToReverseDistributorDetails(ctx: StateContext<OrdersStateModel>, {id}: Act.GetSaleToReverseDistributorDetails): Observable<IOrderSaleReturnDetailsData> {
      ctx.patchState({loading: true, orderSaleOrReturnDetailsData: null});
      return this.http.get(endpoints.order.getSaleToReverseDistributorDetails + "?id=" + id)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({
                orderSaleOrReturnDetailsData: {...responseData?.value, soldReturnedTo: responseData?.value?.reverseDistributorName},
                orderFiles: responseData?.value?.attachments
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetReturnToWholesalerDetails)
    getReturnToWholesalerDetails(ctx: StateContext<OrdersStateModel>, {id}: Act.GetReturnToWholesalerDetails): Observable<IOrderSaleReturnDetailsData> {
      ctx.patchState({loading: true, orderSaleOrReturnDetailsData: null});
      return this.http.get(endpoints.order.getReturnToWholesalerDetails + "?id=" + id)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success) {
              ctx.patchState({
                orderSaleOrReturnDetailsData: {...responseData?.value, soldReturnedTo: responseData?.value?.wholesalerName},
                orderFiles: responseData?.value?.attachments
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetInventoryDestructionDetails)
    getInventoryDestructionDetails(ctx: StateContext<OrdersStateModel>, {id}: Act.GetInventoryDestructionDetails): Observable<IOrderSaleReturnDetailsData> {
      ctx.patchState({loading: true});
      return this.http.get(endpoints.order.getInventoryDestructionDetails + "?id=" + id)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({
                inventoryDestructionDetailsData: {...responseData?.value },
                orderFiles: responseData?.value?.attachments
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetBuyersList)
    getBuyersList(ctx: StateContext<OrdersStateModel>, {}: Act.GetBuyersList): Observable<OrdersStateModel> {
      ctx.patchState({loading: true});
      return this.http.get(endpoints.order.getBuyersList)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({buyers: responseData?.value || []});
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetEdiSuppliersList)
    getEdiSuppliersList(ctx: StateContext<OrdersStateModel>, {}: Act.GetEdiSuppliersList): Observable<OrdersStateModel> {
      ctx.patchState({loading: true});
      return this.http.get(endpoints.order.getEdiSuppliersList)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({ediSuppliers: responseData?.value || []});
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.SaveSupplierBuyer)
    saveSupplierBuyer(ctx: StateContext<OrdersStateModel>, {detail}: Act.SaveSupplierBuyer): Observable<OrdersStateModel> {
      ctx.patchState({loading: true, addedModifiedOrderParty: null, success: false});
      return this.http.put(endpoints.order.createPartyWithDetail, detail)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success) {
              this.handleSaveSupplierBuyerSuccessfulResponse(ctx, detail, responseData?.value);
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    private handleSaveSupplierBuyerSuccessfulResponse(ctx: StateContext<OrdersStateModel>, detail: OrderPartyDetail, responseValue: ISelectedOrderParty): void {
      if (detail.orderParty.type === OrderPartyTypeEnum.Buyer) {
        detail.orderParty.id = responseValue?.orderPartyID;
        const buyers = [...ctx.getState().buyers || []];
        const detailIdx = buyers.findIndex(x => x.id === detail.orderParty.id && x.type === OrderPartyTypeEnum.Buyer);
        if (detailIdx === -1 ) {
          buyers.unshift(detail.orderParty);
        }
        ctx.patchState({buyers});
      } else if (detail.orderParty.type === OrderPartyTypeEnum.Supplier) {
        detail.orderParty.id = responseValue.orderPartyID;
        detail.id =  responseValue?.orderPartyDetailID;
        ctx.patchState({latestSupplierOrEdiBuyerDetails: detail});
      }
      else if (detail.orderParty.type === OrderPartyTypeEnum.EdiSupplier) {
        detail.orderParty.id = responseValue?.orderPartyID;
        const suppliers = [...ctx.getState().ediSuppliers || []];
        const detailIdx = suppliers.findIndex(x => x.id === detail.orderParty.id && x.type === OrderPartyTypeEnum.EdiSupplier);
        if (detailIdx === -1 ) {
          suppliers.unshift(detail.orderParty);
        }
        ctx.patchState({ediSuppliers: suppliers});
      }
      ctx.patchState({
        addedModifiedOrderParty: responseValue,
        success: true
      });
    }

    @Action(Act.GetSelectedPartyLatestDetailsByID)
    getSelectedPartyLatestDetailsByID(ctx: StateContext<OrdersStateModel>, {id}: Act.GetSelectedPartyLatestDetailsByID): Observable<OrdersStateModel> {
      ctx.patchState({loading: true});
      return this.http.get(endpoints.order.getPartyDetails + '/' + id)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({orderPartyDetails: responseData?.value});
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetPartyLatestDetails)
    getPartyLatestDetails(ctx: StateContext<OrdersStateModel>, {type}: Act.GetPartyLatestDetails): Observable<OrdersStateModel> {
      ctx.patchState({loading: true});
      return this.http.get(endpoints.order.getPartyLatestDetails + type)
        .pipe(
          tap((responseData: any) => {
            ctx.patchState({
              loading: false,
            });
            if(responseData?.success){
              ctx.patchState({latestSupplierOrEdiBuyerDetails: responseData?.value});
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.SetPartyDetail)
    setPartyDetails(ctx: StateContext<OrdersStateModel>, {detail}: Act.SetPartyDetail): void {
      ctx.patchState({orderPartyDetails: detail});
    }

    @Action(Act.ClearOrdersState)
    clearState(ctx: StateContext<OrdersStateModel>, {}: Act.ClearOrdersState): any {
      ctx.setState(null);
    }

    @Action(Act.SetLoading)
    setLoading(ctx: StateContext<OrdersStateModel>, {isLoading}: Act.SetLoading): any {
      ctx.patchState({loading: isLoading});
    }
}
